import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import SearchIcon from "@material-ui/icons/Search";
import utils from "../../universal/utils";

const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const MAX_IMAGE_CANDIDATES = 8;

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  searchField: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  summary: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: "70vh",
  },
  productCell: {
    display: "flex",
    alignItems: "center",
  },
  productText: {
    marginLeft: theme.spacing(1.5),
  },
  thumb: {
    width: 48,
    height: 48,
    objectFit: "contain",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f9fafb",
    flexShrink: 0,
  },
  thumbPlaceholder: {
    width: 48,
    height: 48,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f3f4f6",
    flexShrink: 0,
  },
  chip: {
    marginLeft: theme.spacing(0.5),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  dialogHint: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  currentImage: {
    width: 120,
    height: 120,
    objectFit: "contain",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f9fafb",
    marginBottom: theme.spacing(2),
  },
  candidateGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  candidate: {
    width: 108,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: theme.spacing(1),
    background: "#fff",
    cursor: "pointer",
    textAlign: "center",
    font: "inherit",
    color: "inherit",
  },
  candidateSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  candidateImage: {
    width: "100%",
    height: 84,
    objectFit: "contain",
    background: "#f9fafb",
  },
  candidateLabel: {
    marginTop: theme.spacing(0.5),
  },
});

class CatalogSearch extends Component {
  state = {
    query: "",
    products: [],
    loading: false,
    error: "",
    searched: false,
    imageEditor: null,
  };

  componentWillUnmount() {
    this.cancelled = true;
  }

  search = async (event) => {
    if (event) event.preventDefault();
    const query = this.state.query.trim();
    if (query.length < 2) {
      this.setState({
        error: "הקלידו לפחות שני תווים",
        products: [],
        searched: false,
      });
      return;
    }

    this.setState({ loading: true, error: "", searched: true });
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/catalog/search?q=${encodeURIComponent(query)}`,
        { headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();
      if (this.cancelled) return;
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "החיפוש נכשל");
      }
      this.setState({ products: data.products || [], loading: false });
    } catch (err) {
      if (this.cancelled) return;
      this.setState({
        products: [],
        loading: false,
        error: err.message || "החיפוש נכשל",
      });
    }
  };

  openImageEditor = (product) => {
    this.setState({
      imageEditor: {
        product,
        candidates: [],
        mainId: "",
        saving: false,
        error: "",
      },
    });
  };

  closeImageEditor = () => {
    if (this.state.imageEditor && this.state.imageEditor.saving) return;
    this.setState({ imageEditor: null });
  };

  onPickImages = (event) => {
    const fileList = Array.from(event.target.files || []).slice(0, MAX_IMAGE_CANDIDATES);
    event.target.value = "";
    if (!fileList.length || !this.state.imageEditor) return;

    const invalid = fileList.find(
      (file) => !file.type.startsWith("image/") || file.size > MAX_IMAGE_BYTES
    );
    if (invalid) {
      this.setState((prev) => ({
        imageEditor: {
          ...prev.imageEditor,
          error: invalid.type.startsWith("image/")
            ? "כל תמונה יכולה להיות עד 12MB"
            : "אפשר להעלות רק קבצי תמונה",
        },
      }));
      return;
    }

    Promise.all(
      fileList.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                id: `${file.name}-${file.size}-${file.lastModified}`,
                name: file.name,
                dataUrl: reader.result,
              });
            reader.onerror = () => reject(new Error("קריאת הקובץ נכשלה"));
            reader.readAsDataURL(file);
          })
      )
    )
      .then((candidates) => {
        if (this.cancelled || !this.state.imageEditor) return;
        this.setState((prev) => ({
          imageEditor: {
            ...prev.imageEditor,
            candidates,
            mainId: candidates[0] ? candidates[0].id : "",
            error: "",
          },
        }));
      })
      .catch((err) => {
        if (this.cancelled || !this.state.imageEditor) return;
        this.setState((prev) => ({
          imageEditor: {
            ...prev.imageEditor,
            error: err.message || "קריאת הקובץ נכשלה",
          },
        }));
      });
  };

  setMainImage = (mainId) => {
    this.setState((prev) => ({
      imageEditor: prev.imageEditor ? { ...prev.imageEditor, mainId } : null,
    }));
  };

  saveMainImage = async () => {
    const { imageEditor } = this.state;
    if (!imageEditor || imageEditor.saving) return;
    const selected = imageEditor.candidates.find((item) => item.id === imageEditor.mainId);
    if (!selected) {
      this.setState({
        imageEditor: { ...imageEditor, error: "בחרו את התמונה הראשית" },
      });
      return;
    }

    this.setState({ imageEditor: { ...imageEditor, saving: true, error: "" } });
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/catalog/products/${encodeURIComponent(imageEditor.product.ean)}/image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...utils.getAdminAuthHeaders(),
          },
          body: JSON.stringify({ image: selected.dataUrl }),
        }
      );
      const data = await response.json();
      if (this.cancelled) return;
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "החלפת התמונה נכשלה");
      }
      this.setState((prev) => ({
        imageEditor: null,
        products: prev.products.map((product) =>
          product.ean === imageEditor.product.ean && product.source === imageEditor.product.source
            ? { ...product, imageUrl: data.imageUrl }
            : product
        ),
      }));
    } catch (err) {
      if (this.cancelled) return;
      this.setState({
        imageEditor: {
          ...imageEditor,
          saving: false,
          error: err.message || "החלפת התמונה נכשלה",
        },
      });
    }
  };

  renderProduct = (product) => {
    const { classes } = this.props;
    const title = [product.brand, product.displayName].filter(Boolean).join(" ");
    return (
      <div className={classes.productCell}>
        {product.imageUrl ? (
          <img src={product.imageUrl} alt="" className={classes.thumb} />
        ) : (
          <div className={classes.thumbPlaceholder} />
        )}
        <div className={classes.productText}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {title || product.ean}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {product.ean}
          </Typography>
        </div>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const { query, products, loading, error, searched } = this.state;
    const setCount = products.filter((product) => product.set).length;

    return (
      <div className={classes.root}>
        <form className={classes.searchRow} onSubmit={this.search}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            label="חיפוש לפי EAN, מותג או מידה"
            placeholder="לדוגמה: 8019227123456 או Diablo Rosso 180/55"
            value={query}
            onChange={(event) => this.setState({ query: event.target.value })}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            disabled={loading}
          >
            חיפוש
          </Button>
        </form>
        <Typography variant="body2" className={classes.hint}>
          חיפוש לפי EAN מלא או לפי טקסט חופשי. למוצר ממחסן מקומי אפשר להחליף את תמונת המוצר שמוצגת ללקוחות.
        </Typography>
        {error ? (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        ) : null}
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress size={28} />
          </div>
        ) : null}
        {searched && !loading && products.length > 0 ? (
          <Typography variant="body2" className={classes.summary}>
            {`נמצאו ${products.length} שורות, מתוכן ${setCount} סטים`}
          </Typography>
        ) : null}
        {searched && !loading ? (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>מוצר</TableCell>
                <TableCell>מידה</TableCell>
                <TableCell>מחיר</TableCell>
                <TableCell>מלאי</TableCell>
                <TableCell>מקור</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={`${product.source}-${product.ean}`} hover>
                  <TableCell>{this.renderProduct(product)}</TableCell>
                  <TableCell>{product.displaySize}</TableCell>
                  <TableCell>{product.price ? `${product.price} ₪` : ""}</TableCell>
                  <TableCell>{product.stock}</TableCell>
                  <TableCell>
                    {product.set ? (
                      <Chip size="small" label="סט" className={classes.chip} />
                    ) : null}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={product.source === "georgia" ? "מלאי אירופה" : "מחסן"}
                      className={classes.chip}
                    />
                  </TableCell>
                  <TableCell>
                    <div className={classes.actions}>
                      {product.canReplaceImage ? (
                        <Button
                          color="primary"
                          size="small"
                          onClick={() => this.openImageEditor(product)}
                        >
                          החלפת תמונה
                        </Button>
                      ) : null}
                      {product.productPath ? (
                        <Button
                          component="a"
                          href={product.productPath}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          size="small"
                        >
                          עמוד מוצר
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className={classes.empty}>אין תוצאות לחיפוש הזה</div>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
        ) : null}
        {this.renderImageEditor()}
      </div>
    );
  }

  renderImageEditor() {
    const { classes } = this.props;
    const { imageEditor } = this.state;
    if (!imageEditor) return null;

    const { product, candidates, mainId, saving, error } = imageEditor;
    const title = [product.brand, product.displayName].filter(Boolean).join(" ");

    return (
      <Dialog open onClose={this.closeImageEditor} maxWidth="sm" fullWidth>
        <DialogTitle>החלפת תמונת מוצר</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {title || product.ean}
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            {product.ean}
          </Typography>
          <Typography variant="body2" className={classes.dialogHint}>
            החנות מציגה תמונה אחת לכל מוצר. אם מעלים כמה קבצים, סמנו איזו תמונה תהיה הראשית. היא תועלה ל-S3 ותחליף את התמונה שרואים הלקוחות.
          </Typography>
          {product.imageUrl ? (
            <img src={product.imageUrl} alt="" className={classes.currentImage} />
          ) : (
            <div className={classes.thumbPlaceholder} />
          )}
          <Button variant="outlined" component="label" disabled={saving}>
            בחירת תמונות
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={this.onPickImages}
            />
          </Button>
          {candidates.length > 0 ? (
            <div className={classes.candidateGrid}>
              {candidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className={`${classes.candidate} ${
                    candidate.id === mainId ? classes.candidateSelected : ""
                  }`}
                  onClick={() => this.setMainImage(candidate.id)}
                >
                  <img src={candidate.dataUrl} alt="" className={classes.candidateImage} />
                  <Typography variant="caption" className={classes.candidateLabel}>
                    {candidate.id === mainId ? "ראשית" : "בחירה כראשית"}
                  </Typography>
                </button>
              ))}
            </div>
          ) : null}
          {error ? (
            <Typography variant="body2" className={classes.error}>
              {error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeImageEditor} disabled={saving}>
            ביטול
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.saveMainImage}
            disabled={saving || candidates.length === 0}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : "העלאה והחלפה"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withStyles(styles)(CatalogSearch);
